import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuditProbe } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { IdentityProvider } from '@/context/IdentityContext';
import { IngressPlanProvider } from '@/context/IngressPlanContext';
import { AppShell } from './AppShell';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { SkeletonRow } from '@/components/SkeletonRow';

/**
 * Gate every authenticated route on the auth probe at
 * `GET /api/v1/audit/events?limit=1`.
 *
 *   - pending  → full-screen skeleton (fade-in 200ms)
 *   - 401      → redirect to /login
 *   - 403      → render NoAccessPage in-place (no redirect)
 *   - 200      → wrap children in IdentityProvider so the rest of the app
 *                can read isPlatformAdmin without a second probe
 *
 * The 200-implies-platform_admin shortcut is safe because the audit endpoint
 * is itself gated on the `platform_admin` role (src/http.rs:481-492). The day
 * the backend opens it up, this inference must be revisited.
 */
export function ProtectedRoute() {
  const probe = useAuditProbe();

  if (probe.isPending) {
    return (
      <motion.div
        className="flex min-h-screen items-center justify-center bg-[#F4F5F7] px-container-padding"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="w-full max-w-2xl rounded-lg border border-outline-variant bg-surface p-container-padding">
          <SkeletonRow count={6} cells={4} />
        </div>
      </motion.div>
    );
  }

  if (probe.isError) {
    const err = probe.error;
    if (err instanceof ApiError) {
      if (err.isUnauthorized) return <Navigate to="/login" replace />;
      if (err.isForbidden) return <NoAccessPage />;
    }
    // Network / 5xx: surface as NoAccess fallback so the user is not stuck.
    return <NoAccessPage />;
  }

  return (
    <IdentityProvider value={{ isPlatformAdmin: true, principalDisplayName: null }}>
      <IngressPlanProvider>
        <AppShell />
      </IngressPlanProvider>
    </IdentityProvider>
  );
}
